import type { DialogContentNode, DialogTabSaveContext, DialogTabSaveHandler } from "@/v2/framework/dialog/types";
import { getAtPath, setAtPath } from "@/v2/framework/dialog/composables/useDialogContext";
import { mergeEntityPatches } from "@/v2/framework/utils/mergeEntityPatches";

import type { SourcePolicyValue } from "../types";
import { buildInheritablePatch } from "./buildPatch";
import { collectInheritanceDescriptors } from "./collectInheritance";
import { inheritanceTabStateRef } from "./inheritanceTabStateRef";
import { readEntitySlice } from "./entitySlice";

export type InheritableTabSaveOptions = {
  children: DialogContentNode[];
  persist: (patch: Record<string, unknown>, ctx: DialogTabSaveContext) => Promise<void>;
  buildExtraPatch?: (ctx: DialogTabSaveContext) => Record<string, unknown> | null;
  afterPersist?: (ctx: DialogTabSaveContext) => void;
};

function cloneBaselineValue(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  return JSON.parse(JSON.stringify(value)) as unknown;
}

function baselineSliceFor(
  baseline: Record<string, unknown>,
  entityPath: string | undefined,
): Record<string, unknown> {
  if (!entityPath) return baseline;

  const existing = readEntitySlice(baseline, entityPath);
  if (existing) return existing;

  const created: Record<string, unknown> = {};
  baseline[entityPath] = created;
  return created;
}

function syncDescriptorBaseline(
  ctx: DialogTabSaveContext,
  descriptors: ReturnType<typeof collectInheritanceDescriptors>,
  policies: Record<string, SourcePolicyValue>,
) {
  for (const descriptor of descriptors) {
    const currentValue = cloneBaselineValue(getAtPath(ctx.values, descriptor.path));
    setAtPath(ctx.baseline, descriptor.path, currentValue);

    const baselineSlice = baselineSliceFor(ctx.baseline, descriptor.entityPath);
    baselineSlice[descriptor.computedKey] = currentValue;
    baselineSlice[descriptor.field] = currentValue;

    const policy = policies[descriptor.path];
    if (policy == null) continue;

    const previous = baselineSlice.sourcePolicyByField;
    const byField =
      previous && typeof previous === "object" && !Array.isArray(previous)
        ? { ...(previous as Record<string, unknown>) }
        : {};
    byField[descriptor.computedKey] = policy;
    baselineSlice.sourcePolicyByField = byField;
  }
}

export function createInheritableTabSave(options: InheritableTabSaveOptions): DialogTabSaveHandler {
  const { children, persist, buildExtraPatch, afterPersist } = options;
  const descriptors = collectInheritanceDescriptors(children);

  return async (ctx: DialogTabSaveContext) => {
    const state = inheritanceTabStateRef.value;

    const { patch, hasChanges } = buildInheritablePatch({
      current: ctx.values,
      baseline: ctx.baseline,
      descriptors,
      currentPolicies: state?.policies.value ?? {},
      baselinePolicies: state?.baselinePolicies.value ?? {},
    });

    const extraPatch = buildExtraPatch?.(ctx) ?? null;
    const mergedPatch = extraPatch ? mergeEntityPatches(patch, extraPatch) : patch;
    const hasExtraChanges = extraPatch != null && Object.keys(extraPatch).length > 0;

    if (!hasChanges && !hasExtraChanges) return;

    await persist(mergedPatch, ctx);
    syncDescriptorBaseline(ctx, descriptors, state?.policies.value ?? {});
    afterPersist?.(ctx);

    if (state) {
      state.baselinePolicies.value = { ...state.policies.value };
    }
  };
}
